import { ApolloClient } from '@apollo/client';
import { QueryClient, useQuery as useReactQuery } from '@tanstack/react-query';
import { generateEnrichedQuery } from '../utils/dynamicQuery.utils';
import { uniq, values } from 'lodash';
import { useApi } from '@backstage/core-plugin-api';
import {
  ConfigurationApi,
  configurationApiRef,
} from '@agilelab/plugin-wb-configuration-common';
import { ConfigSchema } from '@agilelab/plugin-wb-marketplace-common';
import { parseNunjucks } from '@agilelab/plugin-wb-platform-common';

export type MarketplaceConfigurationType =
  ConfigSchema['fe']['displayNameDescriptorField'];

export const QUERY_KEY = ['marketplaceDisplayNameField'];

const STALE_TIME = 1000 * 60 * 5;

const queryFn = (wbConfigApi: ConfigurationApi) => async () => {
  return wbConfigApi.get<MarketplaceConfigurationType>({
    namespace: 'marketplace',
    key: 'fe.displayNameDescriptorField',
  });
};

export const getDisplayNameConfig = (
  wbConfigApi: ConfigurationApi,
  queryClient: QueryClient,
  noCache?: boolean,
) => {
  return queryClient.fetchQuery({
    queryKey: QUERY_KEY,
    queryFn: queryFn(wbConfigApi),
    staleTime: noCache ? 0 : STALE_TIME,
  });
};

export const useDisplayNameConfig = (noCache?: boolean) => {
  const wbConfigApi = useApi(configurationApiRef);

  const { data } = useReactQuery(QUERY_KEY, queryFn(wbConfigApi), {
    staleTime: noCache ? 0 : STALE_TIME,
  });

  return data;
};

function sanitizeKey(key: string): string {
  return key.replace(/[.\(\)\[\]\{\}]/g, '_');
}

const getDisplayNameFromDescriptor = (
  descriptor: Record<string, any>,
  config: MarketplaceConfigurationType,
  sanitize = false,
): string => {
  const parseValue = (field: string) => {
    return parseNunjucks(`{{${field}}}`, descriptor, true);
  };
  const kind = descriptor.kind;
  const key = config.types?.[kind] || config.default;
  const titleField = sanitize ? sanitizeKey(key) : key;

  return (
    parseValue(titleField) ??
    parseValue(config.default) ??
    descriptor.fullyQualifiedName ??
    descriptor.name
  );
};

export const useDisplayName = (descriptor?: Record<string, any>): string => {
  const configuration = useDisplayNameConfig();
  if (!descriptor) return '';

  if (!configuration) return descriptor.name;

  return getDisplayNameFromDescriptor(descriptor, configuration);
};

export const getDisplayNameByDescriptor = (
  configuration: MarketplaceConfigurationType,
  descriptor?: Record<string, any>,
) => {
  if (!descriptor) return '';
  return getDisplayNameFromDescriptor(descriptor, configuration);
};

export const getDisplayNameMap = async (
  ids: string[],
  apolloClient: ApolloClient<any>,
  configuration: MarketplaceConfigurationType,
): Promise<Map<string, string>> => {
  const resultMap = new Map<string, string>();

  const configurationFields = uniq([
    configuration.default,
    ...values(configuration.types),
  ]);

  const queryFields = [
    'kind',
    'fullyQualifiedName',
    'name',
    ...configurationFields,
  ];

  const { data } = await apolloClient.query({
    query: generateEnrichedQuery(
      queryFields.map(f => ({ key: sanitizeKey(f), path: f })),
    ),
    variables: { ids },
    // this must be added to prevent dynamically generated query to cause cache issues
    fetchPolicy: 'no-cache',
  });

  const records = data?.dataProductInstance ?? [];

  for (const item of records) {
    const displayName = getDisplayNameFromDescriptor(item, configuration, true);
    resultMap.set(item.entityId.toString(), displayName);
  }

  return resultMap;
};
